import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor, store } from './store';
import './services/localization/Localization';
import './styles/index.css';
import 'react-multi-carousel/lib/styles.css';
import 'react-mobile-style-picker/dist/index.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import * as Sentry from '@sentry/react';
import MobileAppProvider from './providers/MobileAppProvider';
import type { ErrorEvent } from '@sentry/core/build/types/types-hoist/event';

declare global {
  interface Window {
    Cypress: any;
    store: any;
    gtag: any;
  }
}

if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysOnErrorSampleRate: import.meta.env.PROD ? 1.0 : 0,
    normalizeDepth: 10,
    environment: import.meta.env.MODE,
    attachStacktrace: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    beforeSend: function (event: ErrorEvent) {
      // filter out UnhandledRejection errors that have no information
      if (
        event !== undefined &&
        event.exception !== undefined &&
        event.exception.values !== undefined &&
        event.exception.values.length == 1
      ) {
        const e = event.exception.values[0];

        if (e.type === 'UnhandledRejection' && e.value.includes('Non-Error promise rejection captured with value:')) {
          return null;
        }

        if (e.value.includes('captured as promise rejection')) {
          return null;
        }

        if (
          e.type === 'SecurityError' &&
          e.value ===
            'Third-party iframes are not allowed to request payments unless explicitly allowed via Feature-Policy (payment)'
        ) {
          return null;
        }

        if (
          e.type === 'Error' &&
          ['Failed to load Stripe.js', 'Could not load "places_impl".', 'Could not load "util".'].includes(e.value)
        ) {
          return null;
        }
      }

      return event;
    },
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <MobileAppProvider>
          <App />
        </MobileAppProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
);

if (window.Cypress) {
  window.store = store;
}

reportWebVitals();
